import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  AngebotsId,
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { VhvV2VorvertragVorschaedenResponseData, VorschadenArt } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { IAppState } from '../appState';
import { VhvActions } from '../vhvActions';

export const VORVERTRAG_VORSCHAEDEN_URL = '/privatschutz/api/vhv-v2-vorvertrag-vorschaeden';

export enum VorvertragVorschaedenRequestEvent {
  VORVERTRAG_VORSCHAEDEN_REQUEST_SUCCESSFUL = 'VORVERTRAG_VORSCHAEDEN_REQUEST_SUCCESSFUL',
  VORVERTRAG_VORSCHAEDEN_REQUEST_ERROR = 'VORVERTRAG_VORSCHAEDEN_REQUEST_ERROR',
}

export function* watchVorvertragVorschaedenRequested(): SagaIterator {
  yield takeEvery(
    VhvActions.VORVERTRAG_VORSCHAEDEN_DATA_REQUESTED,
    safeSaga(vorvertragVorschaedenDataRequested, App.VHV_V2),
  );
  yield takeEvery(
    VorvertragVorschaedenRequestEvent.VORVERTRAG_VORSCHAEDEN_REQUEST_SUCCESSFUL,
    safeSaga(vorvertragVorschaedenRequestSuccessful, App.VHV_V2),
  );
  yield takeEvery(
    VorvertragVorschaedenRequestEvent.VORVERTRAG_VORSCHAEDEN_REQUEST_ERROR,
    safeSaga(vorvertragVorschaedenRequestError, App.VHV_V2),
  );
}

export function* vorvertragVorschaedenDataRequested(
  action: GlobalAction<AngebotsId>,
): SagaIterator {
  const state: IAppState = yield select();
  const isErsatzgeschaeft =
    state.vertragsoptionen?.vorvertragsNummern?.length > 0 && !state.vertragsoptionen.beibehalten;

  if (!isErsatzgeschaeft) {
    return;
  }

  const { appId, angebotsId } = action.payload;
  if (appId !== App.VHV_V2 || angebotsId !== state.angebotsId) {
    return;
  }

  yield call(requestVorvertragVorschaeden);
}

export function* requestVorvertragVorschaeden(): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();

  yield put(new VhvActions().showZumAngebotLoadingSpinner(true));

  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().doRequest({
      appId: App.VHV_V2,
      angebotsId: state.angebotsId,
      body: {
        beginndatum: dayjs(state.vertragsoptionen.beginnDatum, DATEFORMAT_LOCAL_STATE).format(
          DATEFORMAT_SHORT_ISO,
        ),
        vorvertragsnummern: (state.vertragsoptionen.vorvertragsNummern ?? []).map(
          vorvertrag => vorvertrag.vertragsnummer,
        ),
      },
      url: VORVERTRAG_VORSCHAEDEN_URL,
      successEvent: VorvertragVorschaedenRequestEvent.VORVERTRAG_VORSCHAEDEN_REQUEST_SUCCESSFUL,
      errorEvent: VorvertragVorschaedenRequestEvent.VORVERTRAG_VORSCHAEDEN_REQUEST_ERROR,
    }),
  );
}

export function* vorvertragVorschaedenRequestSuccessful(
  successAction: DoRequestResponse<VhvV2VorvertragVorschaedenResponseData>,
): SagaIterator {
  yield put(new VhvActions().showZumAngebotLoadingSpinner(false));
  const notExistingVorschaeden = successAction.payload?.notExistingVorschaeden ?? [];
  yield put(
    new VhvActions().showElementarVorschadenErsatzgeschaeft(
      notExistingVorschaeden.includes(VorschadenArt.ELEMENTAR),
    ),
  );
}

export function* vorvertragVorschaedenRequestError(): SagaIterator {
  // no blocking error modal, matching this saga's existing degrade-silently approach —
  // just release the button so the user can continue without the Elementar question
  yield put(new VhvActions().showZumAngebotLoadingSpinner(false));
}
